import React, { useState, useRef, useEffect } from 'react'; // ← ADD useEffect import
import { X, Plus, MoveUp, MoveDown, Crop, RotateCw } from 'lucide-react';
import ImageCropModal from './ImageCropModal';
import ImageRotateModal from './ImageRotateModal';
import { createCroppedImage } from '../utils/cropImage';

const MultiImageUpload = ({ onImagesChange, currentImages = [], error, maxImages = 5 }) => {
  const [images, setImages] = useState(currentImages || []);
  const [isDragging, setIsDragging] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentCropImage, setCurrentCropImage] = useState(null);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [currentRotateImage, setCurrentRotateImage] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const fileInputRef = useRef(null);

  // Track when currentImages prop changes
  useEffect(() => {
    const safeCurrentImages = currentImages || [];
    setImages(safeCurrentImages);
  }, [currentImages]);

  const validateFile = file => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, or WebP)';
    }

    if (file.size > maxSize) {
      return 'File size must be less than 5MB';
    }

    return null;
  };

  const handleFilesSelect = files => {
    const fileArray = Array.from(files);

    // Check if adding these files would exceed max images
    if (images.length + fileArray.length > maxImages) {
      const errorMsg = `Maximum ${maxImages} images allowed`;
      onImagesChange(images, errorMsg);
      return;
    }

    // Validate all files first
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        onImagesChange(images, error);
        return;
      }
    }

    // Add images directly without auto-cropping
    addImagesDirectly(fileArray);
  };

  const addImagesDirectly = fileArray => {
    const newImages = fileArray.map((file, index) => {
      const preview = URL.createObjectURL(file);
      return {
        id: Date.now() + index,
        file: file,
        preview: preview,
        order: images.length + index + 1,
        caption: '',
        isPrimary: images.length === 0 && index === 0,
        isNew: true,
      };
    });

    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    onImagesChange(updatedImages, null);
  };

  const processingNextFile = (fileArray, index) => {
    if (index >= fileArray.length) {
      // All files processed
      setPendingFiles([]);
      setCurrentFileIndex(0);
      return;
    }

    const file = fileArray[index];
    const reader = new FileReader();
    reader.onload = e => {
      setCurrentCropImage({
        file: file,
        src: e.target.result,
        index: index,
        totalFiles: fileArray.length,
      });
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (cropArea, rotation) => {
    if (!currentCropImage) return;

    try {
      const croppedFile = await createCroppedImage(currentCropImage.src, cropArea, rotation);

      // Create preview URL for the cropped image
      const preview = URL.createObjectURL(croppedFile);

      const newImage = {
        id: Date.now() + currentCropImage.index,
        file: croppedFile,
        preview: preview,
        order: images.length + currentCropImage.index + 1,
        caption: '',
        isPrimary: images.length === 0 && currentCropImage.index === 0,
        isNew: true,
      };

      const updatedImages = [...images, newImage];
      setImages(updatedImages);
      onImagesChange(updatedImages, null);

      // Process next file
      const nextIndex = currentFileIndex + 1;
      setCurrentFileIndex(nextIndex);
      setShowCropModal(false);
      setCurrentCropImage(null);

      setTimeout(() => {
        processingNextFile(pendingFiles, nextIndex);
      }, 100);
    } catch (error) {
      console.error('Error cropping image:', error);
      onImagesChange(images, 'Failed to crop image');
      setShowCropModal(false);
      setCurrentCropImage(null);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCurrentCropImage(null);
    setPendingFiles([]);
    setCurrentFileIndex(0);
  };

  const handleCropExisting = image => {
    setCurrentCropImage({
      existingImage: image,
      src: image.preview || image.url,
      isExisting: true,
    });
    setShowCropModal(true);
  };

  const handleExistingCropComplete = async (cropArea, rotation) => {
    if (!currentCropImage?.existingImage) return;

    try {
      const croppedFile = await createCroppedImage(currentCropImage.src, cropArea, rotation);

      // Create preview URL for the cropped image
      const preview = URL.createObjectURL(croppedFile);

      // Update the existing image
      const updatedImages = images.map(img =>
        img.id === currentCropImage.existingImage.id
          ? {
              ...img,
              file: croppedFile,
              preview: preview,
              isNew: true, // Mark as modified
            }
          : img
      );

      setImages(updatedImages);
      onImagesChange(updatedImages, null);

      setShowCropModal(false);
      setCurrentCropImage(null);
    } catch (error) {
      console.error('Error cropping existing image:', error);
      onImagesChange(images, 'Failed to crop image');
      setShowCropModal(false);
      setCurrentCropImage(null);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFilesSelect(files);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = e => {
    const files = Array.from(e.target.files);
    handleFilesSelect(files);
  };

  const removeImage = id => {
    const updatedImages = images.filter(img => img.id !== id);
    // Reorder remaining images
    const reorderedImages = updatedImages.map((img, index) => ({
      ...img,
      order: index + 1,
      isPrimary: index === 0, // First image becomes primary
    }));

    setImages(reorderedImages);
    onImagesChange(reorderedImages, null);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRotateExisting = image => {
    setCurrentRotateImage({
      existingImage: image,
      src: image.preview || image.url,
      isExisting: true,
    });
    setShowRotateModal(true);
  };

  const handleRotateComplete = async rotation => {
    if (!currentRotateImage?.existingImage) {
      setShowRotateModal(false);
      setCurrentRotateImage(null);
      return;
    }

    // If no rotation was applied, just close the modal
    if (rotation === 0 || rotation % 360 === 0) {
      setShowRotateModal(false);
      setCurrentRotateImage(null);
      return;
    }

    try {
      const originalImage = currentRotateImage.existingImage;
      const originalFileName = originalImage.file?.name || 'image.jpg';
      const rotatedFile = await createRotatedImage(
        currentRotateImage.src,
        rotation,
        originalFileName
      );

      // Create preview URL for the rotated image
      const preview = URL.createObjectURL(rotatedFile);

      // Update the existing image
      const updatedImages = images.map(img =>
        img.id === currentRotateImage.existingImage.id
          ? {
              ...img,
              file: rotatedFile,
              preview: preview,
              isNew: true, // Mark as modified
            }
          : img
      );

      setImages(updatedImages);
      onImagesChange(updatedImages, null);

      setShowRotateModal(false);
      setCurrentRotateImage(null);
    } catch (error) {
      console.error('Error rotating image:', error);
      onImagesChange(images, 'Failed to rotate image');
      setShowRotateModal(false);
      setCurrentRotateImage(null);
    }
  };

  const handleRotateCancel = () => {
    setShowRotateModal(false);
    setCurrentRotateImage(null);
  };

  const createRotatedImage = (imageSrc, degrees, originalFileName = 'image.jpg') => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Swap width and height for 90° rotations
        const isNinetyDegreeRotation = degrees % 180 !== 0;
        canvas.width = isNinetyDegreeRotation ? img.height : img.width;
        canvas.height = isNinetyDegreeRotation ? img.width : img.height;

        // Move to center and rotate
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);

        // Draw image centered
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }

            // Preserve original filename or create a meaningful one
            const fileNameWithoutExt = originalFileName.split('.')[0];
            const extension = originalFileName.split('.').pop() || 'jpg';
            blob.name = `${fileNameWithoutExt}-rotated-${degrees}deg.${extension}`;

            resolve(blob);
          },
          'image/jpeg',
          0.9
        );
      };
      img.onerror = reject;
      img.src = imageSrc;
    });
  };

  // Removed setPrimary - first image is always primary

  const moveImage = (id, direction) => {
    const currentIndex = images.findIndex(img => img.id === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= images.length) return;

    const updatedImages = [...images];
    [updatedImages[currentIndex], updatedImages[newIndex]] = [
      updatedImages[newIndex],
      updatedImages[currentIndex],
    ];

    // Update order values and set first image as primary
    const reorderedImages = updatedImages.map((img, index) => ({
      ...img,
      order: index + 1,
      isPrimary: index === 0,
    }));

    setImages(reorderedImages);
    onImagesChange(reorderedImages, null);
  };

  const updateCaption = (id, caption) => {
    const updatedImages = images.map(img => (img.id === id ? { ...img, caption } : img));

    setImages(updatedImages);
    onImagesChange(updatedImages, null);
  };

  return (
    <div className="space-y-4">
      {images.length > 0 && (
        <div className="mb-2 text-center">
          <span className="text-xs text-gray-400">
            First image is primary • Use arrows to reorder • Add captions if needed
            <br />
            <span className="text-sm font-bold text-gray-300">
              Only use original photos, or those unwatermarked from the public domain
            </span>
          </span>
        </div>
      )}

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`bg-gray-800 rounded-lg overflow-hidden border-2 ${
                image.isPrimary ? 'border-red-500' : 'border-gray-700'
              }`}
            >
              {/* Image */}
              <div className="relative">
                <img
                  src={image.preview || image.url}
                  alt={`Release image ${index + 1}`}
                  className="w-full h-44 sm:h-52 object-cover"
                />
                {image.isPrimary && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">
                    Primary
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-black bg-opacity-75 border border-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                  {index + 1}
                </div>
              </div>

              {/* Controls - always visible */}
              <div className="bg-gray-900 border-t border-gray-700 p-2 space-y-2">
                <div className="flex justify-center gap-2">
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => moveImage(image.id, 'up')}
                      className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      title="Move up"
                    >
                      <MoveUp size={16} />
                    </button>
                  )}
                  {index < images.length - 1 && (
                    <button
                      type="button"
                      onClick={() => moveImage(image.id, 'down')}
                      className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      title="Move down"
                    >
                      <MoveDown size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleCropExisting(image)}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    title="Crop image"
                  >
                    <Crop size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRotateExisting(image)}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    title="Rotate image"
                  >
                    <RotateCw size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="p-2 bg-gray-700 text-white rounded hover:bg-red-700 transition-colors"
                    title="Remove image"
                  >
                    <X size={16} />
                  </button>
                </div>
                <input
                  type="text"
                  value={image.caption || ''}
                  onChange={e => updateCaption(image.id, e.target.value)}
                  placeholder="Add caption..."
                  maxLength={40}
                  className="w-full text-xs bg-gray-800 text-white placeholder-gray-400 border border-gray-600 rounded px-2 py-1 focus:border-red-500 focus:outline-none"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-red-500'
              : error
                ? 'border-red-400 bg-red-900 bg-opacity-20'
                : 'border-transparent hover:border-red-500'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInputChange}
            className="hidden"
          />

          <div className="space-y-2">
            <div className="flex justify-center">
              {error ? (
                <X className="h-8 w-8 text-red-400" />
              ) : (
                <Plus className="h-8 w-8 text-gray-400 hover:text-red-500 transition-colors" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-300">
                {error
                  ? 'Error uploading images'
                  : `Add ${images.length === 0 ? '' : 'more '}images`}
              </p>
              <p className="text-xs text-gray-400">
                Click to upload or drag and drop • {maxImages - images.length} remaining
              </p>
              <p className="text-xs text-gray-400">JPEG, PNG, GIF, or WebP up to 5MB each</p>
            </div>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}

      {/* Crop Modal */}
      <ImageCropModal
        isOpen={showCropModal}
        onClose={handleCropCancel}
        imageSrc={currentCropImage?.src}
        onCropComplete={
          currentCropImage?.isExisting ? handleExistingCropComplete : handleCropComplete
        }
        title={
          currentCropImage?.isExisting
            ? 'Crop Image'
            : currentCropImage
              ? `Crop Image ${currentFileIndex + 1} of ${currentCropImage.totalFiles}`
              : 'Crop Image'
        }
      />

      {/* Rotate Modal */}
      <ImageRotateModal
        isOpen={showRotateModal}
        onClose={handleRotateCancel}
        imageSrc={currentRotateImage?.src}
        onRotateComplete={handleRotateComplete}
        title="Rotate Image"
      />
    </div>
  );
};

export default MultiImageUpload;
